import React, { createContext, useState, type ReactNode } from "react";

import { RenameDocumentModal } from "../components/RenameDocumentModal";

export type RenameDocumentReturn = {
  openModal: (
    currentFileName: string,
    onConfirm: (newFileName: string) => Promise<void>,
  ) => void;
};

const RenameDocumentContext = createContext<RenameDocumentReturn | undefined>(
  undefined,
);

export { RenameDocumentContext };

type RenameDocumentProviderProps = {
  children: ReactNode;
};

export const RenameDocumentProvider = ({
  children,
}: RenameDocumentProviderProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentFileName, setCurrentFileName] = useState("");
  const [onConfirmCallback, setOnConfirmCallback] = useState<
    ((newFileName: string) => Promise<void>) | null
  >(null);

  const openModal = (
    fileName: string,
    onConfirm: (newFileName: string) => Promise<void>,
  ): void => {
    setCurrentFileName(fileName);
    setOnConfirmCallback(() => onConfirm);
    setIsOpen(true);
  };

  const handleClose = (): void => {
    setIsOpen(false);
    setCurrentFileName("");
    setOnConfirmCallback(null);
  };

  const handleConfirm = async (newFileName: string): Promise<void> => {
    await onConfirmCallback?.(newFileName);
    handleClose();
  };

  return (
    <RenameDocumentContext.Provider value={{ openModal }}>
      {children}
      <RenameDocumentModal
        isOpen={isOpen}
        currentFileName={currentFileName}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </RenameDocumentContext.Provider>
  );
};
